import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useMemo } from "react";
import type { ModalBaseProps, ModalBaseSize, ModalBaseVariant } from "./types";

const BASE_OVERLAY_CLASS = "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm";

const BASE_CONTENT_CLASS = "fixed z-50 outline-none focus:outline-none";

const VARIANT_CLASSNAMES: Record<ModalBaseVariant, string> = {
  center:
    "inset-0 flex flex-col items-center justify-center p-4 overflow-y-auto",
  "bottom-sheet": "left-0 right-0 bottom-0",
};

const SIZE_CLASSNAMES: Record<ModalBaseSize, string> = {
  sm: "w-full max-w-sm",
  md: "w-full max-w-md",
  lg: "w-full max-w-7xl",
  full: "w-full max-w-none",
};

function cx(...classes: Array<string | undefined | false>) {
  return classes.filter(Boolean).join(" ");
}

function getContentMotionConfig(variant: ModalBaseVariant) {
  if (variant === "bottom-sheet") {
    return {
      initial: { opacity: 0, y: 32, scale: 0.98 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: { opacity: 0, y: 24, scale: 0.98 },
    };
  }

  return {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };
}

export function ModalBase({
  open,
  onOpenChange,
  modal = true,
  children,
  className,
  overlayClassName,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  variant = "center",
  size = "md",
  title,
}: ModalBaseProps & { title?: string }) {
  const containerClassName = useMemo(
    () => cx(BASE_CONTENT_CLASS, VARIANT_CLASSNAMES[variant]),
    [variant],
  );

  const panelClassName = useMemo(
    () => cx(!className && SIZE_CLASSNAMES[size], className),
    [className, size],
  );

  const overlayClass = useMemo(
    () => cx(BASE_OVERLAY_CLASS, overlayClassName),
    [overlayClassName],
  );

  const contentMotion = useMemo(
    () => getContentMotionConfig(variant),
    [variant],
  );

  const handlePointerDownOutside = useCallback(
    (event: Event) => {
      if (!closeOnOverlayClick) {
        event.preventDefault();
        return;
      }
      // Cliques em portais externos ao Radix (react-confirm-alert, react-select,
      // datepicker, etc.) são detectados como "fora do modal" pelo DismissableLayer.
      // Se o alvo não está dentro de nenhum [data-radix-portal], é um portal externo
      // — não deve fechar o modal.
      const target = event.target as Element | null;
      if (target && !target.closest("[data-radix-portal]")) {
        event.preventDefault();
      }
    },
    [closeOnOverlayClick],
  );

  const handleEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!closeOnEsc) {
        event.preventDefault();
      }
    },
    [closeOnEsc],
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <Dialog.Portal forceMount>
        <AnimatePresence>
          {open && (
            <>
              {/* Overlay */}
              <Dialog.Overlay asChild>
                <motion.div
                  key="modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className={overlayClass}
                />
              </Dialog.Overlay>

              {/* Content */}
              <Dialog.Content
                asChild
                aria-describedby={undefined}
                onPointerDownOutside={handlePointerDownOutside}
                onEscapeKeyDown={handleEscapeKeyDown}
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <motion.div
                  key="modal-content"
                  initial={contentMotion.initial}
                  animate={contentMotion.animate}
                  exit={contentMotion.exit}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className={containerClassName}
                  data-variant={variant}
                  data-size={size}
                  onClick={
                    closeOnOverlayClick ? () => onOpenChange(false) : undefined
                  }
                >
                  {title && (
                    <Dialog.Title className="sr-only">{title}</Dialog.Title>
                  )}
                  <div
                    className={panelClassName}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {children}
                  </div>
                </motion.div>
              </Dialog.Content>
            </>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export type { ModalBaseProps, ModalBaseSize, ModalBaseVariant } from "./types";
